import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// Contexto global para toasts
const ToastContext = createContext(null);

// Tipos de toast com seus estilos
const toastStyles = {
  success: {
    bg: 'bg-emerald-50 border-emerald-200',
    icon: CheckCircle,
    iconColor: 'text-emerald-600',
    titleColor: 'text-emerald-900',
    msgColor: 'text-emerald-700'
  },
  error: {
    bg: 'bg-rose-50 border-rose-200',
    icon: XCircle,
    iconColor: 'text-rose-600',
    titleColor: 'text-rose-900',
    msgColor: 'text-rose-700'
  },
  warning: {
    bg: 'bg-amber-50 border-amber-200',
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    titleColor: 'text-amber-900',
    msgColor: 'text-amber-700'
  },
  info: {
    bg: 'bg-blue-50 border-blue-200',
    icon: Info,
    iconColor: 'text-blue-600',
    titleColor: 'text-blue-900',
    msgColor: 'text-blue-700'
  }
};

// Componente individual de Toast
const ToastItem = ({ id, type, title, message, onClose }) => {
  const style = toastStyles[type] || toastStyles.info;
  const Icon = style.icon;
  
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, 4000);
    return () => clearTimeout(timer);
  }, [id, onClose]);

  return (
    <div 
      className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg ${style.bg} animate-slide-in`}
      role="alert"
    >
      <Icon className={`w-5 h-5 ${style.iconColor} shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        {title && <p className={`font-semibold ${style.titleColor}`}>{title}</p>}
        {message && <p className={`text-sm ${style.msgColor}`}>{message}</p>}
      </div>
      <button 
        onClick={() => onClose(id)}
        className="p-1 hover:bg-black/5 rounded transition-colors shrink-0"
        aria-label="Fechar"
      >
        <X className="w-4 h-4 text-slate-500" />
      </button>
    </div>
  );
};

// Container de Toasts - NÃO usa Portal para evitar conflitos DOM
export const ToastContainer = () => {
  const context = useContext(ToastContext);
  if (!context) return null;
  
  const { toasts, removeToast } = context;
  
  if (toasts.length === 0) return null;
  
  return (
    <div 
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none"
      aria-live="polite"
    >
      {toasts.map(toast => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem {...toast} onClose={removeToast} />
        </div>
      ))}
    </div>
  );
};

// Provider de Toasts
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  
  const addToast = useCallback((type, titleOrMessage, messageOrOptions) => {
    const id = Date.now() + Math.random();

    // Defensive normalization:
    // - toast.error("msg", { duration: X }) is a common API in sonner/sanic.
    //   We accept it but ignore the options object (duration, etc.) because
    //   this custom toast component doesn't support them yet.
    // - toast.error("title", "message") keeps working as before.
    // - If title/message is a non-string object (e.g. Axios error.detail),
    //   serialize it safely instead of rendering it directly (React crash).
    const serialize = (v) => {
      if (v == null) return null;
      if (typeof v === 'string') return v;
      if (Array.isArray(v)) {
        return v.map((x) => (typeof x === 'string' ? x : (x?.msg || JSON.stringify(x)))).join('; ');
      }
      if (typeof v === 'object') {
        // Ignore plain options objects (duration, action, etc.) — they aren't messages
        const keys = Object.keys(v);
        if (keys.length > 0 && keys.every((k) => ['duration', 'action', 'onClick', 'dismissible', 'id', 'position', 'icon'].includes(k))) {
          return null;
        }
        return v.msg || v.message || JSON.stringify(v);
      }
      return String(v);
    };

    const normalizedTitle = serialize(titleOrMessage);
    const normalizedMessage = serialize(messageOrOptions);

    const toast = {
      id,
      type,
      title: normalizedMessage ? normalizedTitle : null,
      message: normalizedMessage || normalizedTitle,
    };
    setToasts(prev => [...prev.slice(-4), toast]); // Máximo 5 toasts
    return id;
  }, []);
  
  const value = {
    toasts,
    removeToast,
    success: (title, msg) => addToast('success', title, msg),
    error: (title, msg) => addToast('error', title, msg),
    warning: (title, msg) => addToast('warning', title, msg),
    info: (title, msg) => addToast('info', title, msg)
  };
  
  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

// Hook para usar toasts
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    // Fallback para uso fora do provider (não deve acontecer)
    return {
      success: (msg) => console.log('[Toast Success]', msg),
      error: (msg) => console.error('[Toast Error]', msg),
      warning: (msg) => console.warn('[Toast Warning]', msg),
      info: (msg) => console.info('[Toast Info]', msg)
    };
  }
  return context;
};

// Funções globais para uso sem hook (útil em arquivos não-React)
let globalToastFn = null;

export const setGlobalToast = (toastFn) => {
  globalToastFn = toastFn;
};

export const toast = {
  success: (msg) => globalToastFn?.success(msg),
  error: (msg) => globalToastFn?.error(msg),
  warning: (msg) => globalToastFn?.warning(msg),
  info: (msg) => globalToastFn?.info(msg)
};

// Componente para inicializar toast global
export const ToastInitializer = () => {
  const toastContext = useToast();
  
  useEffect(() => {
    setGlobalToast(toastContext);
  }, [toastContext]);
  
  return null;
};

export default { ToastProvider, ToastContainer, useToast, toast, ToastInitializer };
